import React, { useState } from 'react';
import './MorePage.css';
import SignOutConfirmSheet from '../components/SignOutConfirmSheet';

const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="7,4 13,10 7,16"/>
  </svg>
);

// Google G icon
const GoogleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const SECTIONS = [
  {
    title: 'Your app',
    items: [
      {
        id: 'scan',
        label: 'Scan receipt',
        sub: 'Help keep prices accurate (and improve your results)',
        icon: (
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="12" height="16" rx="2"/>
            <line x1="7" y1="7" x2="13" y2="7"/>
            <line x1="7" y1="10" x2="13" y2="10"/>
            <line x1="7" y1="13" x2="10" y2="13"/>
          </svg>
        ),
        iconBg: '#fef9e7',
        iconColor: '#b45309',
      },
      {
        id: 'scan_history',
        label: 'Past scans',
        sub: 'Your past shops, ready to reuse.',
        icon: (
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="10" cy="10" r="8"/>
            <polyline points="10,6 10,10 13,12"/>
          </svg>
        ),
        iconBg: '#fef9e7',
        iconColor: '#b45309',
      },
    ],
  },
  {
    title: 'About & help',
    items: [
      {
        id: 'about',
        label: 'About ChopMyShop',
        sub: 'How it works and how we keep prices reliable',
        icon: (
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="10" cy="10" r="8"/>
            <line x1="10" y1="9" x2="10" y2="14"/>
            <circle cx="10" cy="6.5" r="0.75" fill="currentColor" stroke="none"/>
          </svg>
        ),
        iconBg: '#e8f4fd',
        iconColor: '#2563eb',
      },
      {
        id: 'beta_feedback',
        label: 'Share your experience',
        sub: "A quick check-in while you're here",
        icon: (
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 2a8 8 0 100 16A8 8 0 0010 2z"/>
            <path d="M7 9h6M7 12h4"/>
          </svg>
        ),
        iconBg: '#f3e6ff',
        iconColor: '#7c3aed',
      },
      {
        id: 'feedback',
        label: 'Feedback',
        sub: 'Help us improve ChopMyShop or report an issue',
        icon: (
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h16a1 1 0 011 1v9a1 1 0 01-1 1H6l-4 3V4a1 1 0 011-1z"/>
          </svg>
        ),
        iconBg: '#fef3e2',
        iconColor: '#d97706',
      },
      {
        id: 'legal',
        label: 'Privacy & Terms',
        sub: 'Privacy policy, terms of use, data',
        icon: (
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 2L3 5v5c0 4.4 3 8.2 7 9 4-0.8 7-4.6 7-9V5L10 2z"/>
          </svg>
        ),
        iconBg: '#f0fdf4',
        iconColor: '#16a34a',
      },
    ],
  },
];

function MorePage({ onNavigate, user, onSignIn, onUpgrade, onSignOut, onOpenScan }) {
  const [signOutSheetOpen, setSignOutSheetOpen] = useState(false);
  const [upgrading,         setUpgrading]         = useState(false);
  const [accountError,      setAccountError]      = useState(null);

  const isGuest      = !user || user.isAnonymous;
  const displayEmail = user?.email || null;
  const initials     = displayEmail ? displayEmail[0].toUpperCase() : '?';

  const handleUpgrade = async () => {
    setUpgrading(true);
    setAccountError(null);
    try {
      await onUpgrade();
      // Page will redirect to Google — loading state stays until redirect
    } catch (err) {
      setAccountError('Could not sign in. Please try again.');
      setUpgrading(false);
    }
  };

  const handleSignOut = async () => {
    await onSignOut();
    setSignOutSheetOpen(false);
  };

  return (
    <div className="more-page">

      {/* ── Account section ── */}
      <div className="more-section more-account-section">
        {isGuest ? (
          /* Guest state */
          <>
            <div className="more-account-guest-header">
              <span className="more-account-guest-badge">Guest mode</span>
              <span className="more-account-guest-title">You're browsing as a guest</span>
              <span className="more-account-guest-sub">Your lists are saved on this device only</span>
            </div>
            <div className="more-account-guest-cta">
              <button
                className="more-account-google-btn"
                onClick={handleUpgrade}
                disabled={upgrading}
              >
                {upgrading ? <span className="more-account-spinner" /> : <GoogleIcon />}
                Save my account with Google
              </button>
            </div>
          </>
        ) : (
          /* Signed-in state */
          <div className="more-account-signed-in">
            <div className="more-account-avatar more-account-avatar--real">
              {user.photoURL ? (
                <img src={user.photoURL} alt="" className="more-account-photo" />
              ) : (
                <span className="more-account-initials">{initials}</span>
              )}
            </div>
            <div className="more-account-info">
              <span className="more-account-status">Signed in</span>
              <span className="more-account-name">{user.displayName || displayEmail}</span>
              {user.displayName && (
                <span className="more-account-sub">{displayEmail}</span>
              )}
            </div>
            <button
              className="more-account-signout-btn"
              onClick={() => setSignOutSheetOpen(true)}
            >
              Sign out
            </button>
          </div>
        )}
        {accountError && <p className="more-account-error">{accountError}</p>}
      </div>

      {SECTIONS.map((section) => (
        <div key={section.title} className="more-section">
          {section.title && (
            <p className="more-section-title">{section.title}</p>
          )}
          {section.items.map((item) => (
            <button
              key={item.id}
              className="more-row"
              onClick={
                item.id === 'scan'         ? () => onOpenScan?.()                                    :
                item.id === 'scan_history' ? () => onNavigate && onNavigate('past-scans')         :
                item.id === 'beta_feedback'? () => onNavigate && onNavigate('beta-feedback')      :
                item.id === 'feedback'     ? () => onNavigate && onNavigate('feedback')            :
                item.id === 'legal'        ? () => onNavigate && onNavigate('legal')          :
                item.id === 'about'        ? () => onNavigate && onNavigate('about')          :
                undefined
              }
            >
              <span
                className="more-row-icon"
                style={{ background: item.iconBg, color: item.iconColor }}
              >
                {item.icon}
              </span>
              <span className="more-row-text">
                <span className="more-row-label">{item.label}</span>
                {item.sub && <span className="more-row-sub">{item.sub}</span>}
              </span>
              <span className="more-row-chevron">
                <ChevronRight />
              </span>
            </button>
          ))}
        </div>
      ))}

      {/* ── Admin (bottom, internal only) ── */}
      {displayEmail === 'hello.chopmyshop@gmail.com' && (
        <div className="more-section more-admin-section">
          <button className="more-row" onClick={() => onNavigate && onNavigate('admin')}>
            <span className="more-row-icon" style={{ background: '#f3e6ff', color: '#7c3aed' }}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="10" cy="5" rx="8" ry="3"/>
                <path d="M2 5v5c0 1.66 3.58 3 8 3s8-1.34 8-3V5"/>
                <path d="M2 10v5c0 1.66 3.58 3 8 3s8-1.34 8-3v-5"/>
              </svg>
            </span>
            <span className="more-row-text">
              <span className="more-row-label">Admin</span>
              <span className="more-row-sub">Review price changes, manage products and requests</span>
            </span>
            <span className="more-row-chevron">
              <ChevronRight />
            </span>
          </button>
        </div>
      )}

      <p className="more-version">ChopMyShop · v0.1 MVP</p>

      <SignOutConfirmSheet
        isOpen={signOutSheetOpen}
        onConfirm={handleSignOut}
        onCancel={() => setSignOutSheetOpen(false)}
      />

    </div>
  );
}

export default MorePage;
